'use client';
import { usePreferences, Language } from './preferences';

type Dict = Record<string, string>;

const EN: Dict = {
  'app.title': 'Musicska',
  'app.subtitle': 'Interactive note training for instruments',
  'app.subtitleHint': 'You can upload image, pdf or mxl file for playing or training',
  'app.signUp': 'Sign up for competition',
  'nav.home': 'Home',
  'nav.profile': 'Profile',
  'home.uploadImage': 'Upload image file (.jpg, .png, .pdf)',
  'home.uploadImageHint': 'Pick a sheet music image or PDF to display',
  'home.uploadImageClear': 'Remove image',
  'home.converting': 'Converting image to MusicXML via Audiveris...',
  'home.convertOk': 'Conversion complete — score loaded',
  'home.convertErr': 'Conversion failed',
  'home.convertErr.LOW_RESOLUTION': 'Image resolution is too low for note recognition. Use a higher-resolution scan (about 300 DPI) or upload the PDF instead.',
  'home.convertErr.NO_SCORE': 'No readable sheet music (staff lines) was detected in this file.',
  'home.convertErr.CONVERT_FAILED': 'Could not convert this file. Make sure it is clear, printed sheet music.',
  'home.upload': 'Upload .mxl file',
  'home.uploadHint': 'Click to pick a compressed MusicXML (.mxl) from your computer',
  'home.noScore': 'No score loaded yet. Upload an .mxl file to begin.',
  'home.bpm': 'Tempo (BPM)',
  'home.play': 'Play',
  'home.stop': 'Stop',
  'home.record': 'Test Yourself',
  'home.stopRecord': 'Stop recording',
  'home.reset': 'Reset',
  'home.micStart': 'Start microphone',
  'home.micStop': 'Stop microphone',
  'home.currentNote': 'Current note',
  'home.target': 'Target',
  'home.detected': 'Detected',
  'home.waiting': 'Waiting for sound...',
  'home.completed': 'Finished!',
  'home.correct': 'Correct',
  'home.accuracy': 'Accuracy',
  'home.totalScore': 'Total score',
  'home.micError': 'Microphone error',
  'home.loadError': 'Could not load score',
  'home.flat': 'flat',
  'home.sharp': 'sharp',
  'home.metronome': 'Metronome',
  'home.loading': 'Loading score...',
  'home.countdown': 'Get ready...',
  'home.prevNote': 'Prev',
  'home.nextNote': 'Next',
  'profile.title': 'Profile',
  'profile.language': 'Interface language',
  'profile.noteNaming': 'Note naming',
  'profile.noteNaming.abc': 'Letter names (A4, B4, C4)',
  'profile.noteNaming.dorm': 'Solfège (do4, re4, mi4)',
  'profile.defaultBpm': 'Default tempo (BPM)',
  'profile.saved': 'Saved',
  'profile.country': 'Country',
  'profile.countryNone': 'Select country',
  'profile.instrument': 'Instrument',
  'profile.instrumentNone': 'Select instrument',
  'profile.myScores': 'My Recordings',
  'profile.noScores': 'No recordings yet',
  'profile.song': 'Song',
  'profile.myFiles': 'My Scores',
  'profile.noFiles': 'No uploaded scores yet',
  'instrument.flute': 'Flute',
  'instrument.violin': 'Violin',
  'instrument.piano': 'Piano',
  'instrument.guitar': 'Guitar',
  'instrument.cello': 'Cello',
  'instrument.trumpet': 'Trumpet',
  'instrument.saxophone': 'Saxophone',
  'instrument.clarinet': 'Clarinet',
  'instrument.oboe': 'Oboe',
  'instrument.voice': 'Voice / Singing',
  'leaderboard.byCountry': 'By Country',
  'leaderboard.byInstrument': 'By Instrument',
  'leaderboard.selectCountry': 'Select a country',
  'leaderboard.selectInstrument': 'Select an instrument',
  'lang.en': 'English',
  'lang.tr': 'Türkçe',
  'lang.fr': 'Français',
  'lang.de': 'Deutsch',
  'lang.es': 'Español',
  'lang.it': 'Italiano',
  'lang.zh': '中文',
  'lang.ja': '日本語',
  'lang.ko': '한국어',
  'lang.hi': 'हिन्दी',
  'lang.ru': 'Русский',
  'lang.ar': 'العربية',
  'lang.pt': 'Português',
  'auth.login': 'Sign In',
  'auth.logout': 'Sign Out',
  'auth.register': 'Register',
  'auth.email': 'Email',
  'auth.password': 'Password',
  'auth.name': 'Name',
  'auth.noAccount': 'No account?',
  'auth.hasAccount': 'Already have an account?',
  'auth.loginGoogle': 'Sign in with Google',
  'auth.loginError': 'Invalid email or password',
  'auth.forgotPassword': 'Forgot password?',
  'auth.forgotPasswordHint': 'Enter your email and we will send you a reset link.',
  'auth.forgotPasswordSent': 'If an account exists for this email, a reset link has been sent.',
  'auth.sendResetLink': 'Send Reset Link',
  'auth.sending': 'Sending...',
  'auth.backToLogin': 'Back to login',
  'auth.resetPassword': 'Reset Password',
  'auth.newPassword': 'New Password',
  'auth.confirmPassword': 'Confirm Password',
  'auth.passwordMismatch': 'Passwords do not match',
  'auth.passwordTooShort': 'Password must be at least 6 characters',
  'auth.resetSuccess': 'Password updated! Redirecting to login...',
  'auth.resetInvalidLink': 'Invalid or missing reset token.',
  'auth.saving': 'Saving...',
  'nav.leaderboard': 'Leaderboard',
  'leaderboard.title': 'Leaderboard',
  'leaderboard.bySong': 'By Song',
  'leaderboard.global': 'Global',
  'leaderboard.rank': 'Rank',
  'leaderboard.player': 'Player',
  'leaderboard.score': 'Score',
  'leaderboard.accuracy': 'Accuracy',
  'leaderboard.date': 'Date',
};

const TR: Dict = {
  'app.title': 'Musicska',
  'app.subtitle': 'Enstrümanlar için interaktif nota eğitimi',
  'app.subtitleHint': 'Çalmak veya eğitim için resim, pdf veya mxl dosyası yükleyebilirsiniz',
  'app.signUp': 'Yarışmaya katılmak için kayıt ol',
  'nav.home': 'Ana Sayfa',
  'nav.profile': 'Profil',
  'home.uploadImage': 'Resim dosyası yükle (.jpg, .png, .pdf)',
  'home.uploadImageHint': 'Görüntülenecek bir nota resmi veya PDF seçin',
  'home.uploadImageClear': 'Resmi kaldır',
  'home.converting': 'Audiveris ile görüntü MusicXML dosyasına dönüştürülüyor...',
  'home.convertOk': 'Dönüştürme tamamlandı — nota yüklendi',
  'home.convertErr': 'Dönüştürme başarısız',
  'home.convertErr.LOW_RESOLUTION': 'Görüntü çözünürlüğü nota tanıma için çok düşük. Daha yüksek çözünürlüklü bir tarama (yaklaşık 300 DPI) kullanın veya PDF olarak yükleyin.',
  'home.convertErr.NO_SCORE': 'Bu dosyada okunabilir nota (porte çizgileri) bulunamadı.',
  'home.convertErr.CONVERT_FAILED': 'Bu dosya dönüştürülemedi. Net, basılı bir nota olduğundan emin olun.',
  'home.upload': '.mxl dosyası yükle',
  'home.uploadHint': 'Bilgisayarınızdan bir sıkıştırılmış MusicXML (.mxl) seçin',
  'home.noScore': 'Henüz bir şarkı yüklenmedi. Başlamak için .mxl dosyası yükleyin.',
  'home.bpm': 'Tempo (BPM)',
  'home.play': 'Çal',
  'home.stop': 'Durdur',
  'home.record': 'Kendini Test Et',
  'home.stopRecord': 'Kaydı durdur',
  'home.reset': 'Sıfırla',
  'home.micStart': 'Mikrofonu başlat',
  'home.micStop': 'Mikrofonu durdur',
  'home.currentNote': 'Şu anki nota',
  'home.target': 'Hedef',
  'home.detected': 'Duyulan',
  'home.waiting': 'Ses bekleniyor...',
  'home.completed': 'Tebrikler!',
  'home.correct': 'Doğru',
  'home.accuracy': 'Doğruluk',
  'home.totalScore': 'Toplam puan',
  'home.micError': 'Mikrofon hatası',
  'home.loadError': 'Nota yüklenemedi',
  'home.flat': 'düşük',
  'home.sharp': 'yüksek',
  'home.metronome': 'Metronom',
  'home.loading': 'Nota yükleniyor...',
  'home.countdown': 'Hazır ol...',
  'home.prevNote': 'Önceki',
  'home.nextNote': 'Sonraki',
  'profile.title': 'Profil',
  'profile.language': 'Arayüz dili',
  'profile.noteNaming': 'Nota adlandırma',
  'profile.noteNaming.abc': 'Harf adları (A4, B4, C4)',
  'profile.noteNaming.dorm': 'Solfej (do4, re4, mi4)',
  'profile.defaultBpm': 'Varsayılan tempo (BPM)',
  'profile.saved': 'Kaydedildi',
  'profile.country': 'Ülke',
  'profile.countryNone': 'Ülke seçin',
  'profile.instrument': 'Enstrüman',
  'profile.instrumentNone': 'Enstrüman seçin',
  'profile.myScores': 'Kayıtlarım',
  'profile.noScores': 'Henüz kayıt yok',
  'profile.song': 'Şarkı',
  'profile.myFiles': 'Notalarım',
  'profile.noFiles': 'Henüz yüklenmiş nota yok',
  'instrument.flute': 'Flüt',
  'instrument.violin': 'Keman',
  'instrument.piano': 'Piyano',
  'instrument.guitar': 'Gitar',
  'instrument.cello': 'Çello',
  'instrument.trumpet': 'Trompet',
  'instrument.saxophone': 'Saksofon',
  'instrument.clarinet': 'Klarnet',
  'instrument.oboe': 'Obua',
  'instrument.voice': 'Ses / Şan',
  'leaderboard.byCountry': 'Ülke Bazında',
  'leaderboard.byInstrument': 'Enstrüman Bazında',
  'leaderboard.selectCountry': 'Ülke seçin',
  'leaderboard.selectInstrument': 'Enstrüman seçin',
  'lang.en': 'English',
  'lang.tr': 'Türkçe',
  'lang.fr': 'Français',
  'lang.de': 'Deutsch',
  'lang.es': 'Español',
  'lang.it': 'Italiano',
  'lang.zh': '中文',
  'lang.ja': '日本語',
  'lang.ko': '한국어',
  'lang.hi': 'हिन्दी',
  'lang.ru': 'Русский',
  'lang.ar': 'العربية',
  'lang.pt': 'Português',
  'auth.login': 'Giriş Yap',
  'auth.logout': 'Çıkış Yap',
  'auth.register': 'Kayıt Ol',
  'auth.email': 'E-posta',
  'auth.password': 'Şifre',
  'auth.name': 'İsim',
  'auth.noAccount': 'Hesabınız yok mu?',
  'auth.hasAccount': 'Zaten hesabınız var mı?',
  'auth.loginGoogle': 'Google ile Giriş Yap',
  'auth.loginError': 'Geçersiz e-posta veya şifre',
  'auth.forgotPassword': 'Şifremi unuttum?',
  'auth.forgotPasswordHint': 'E-postanızı girin, sıfırlama bağlantısı gönderelim.',
  'auth.forgotPasswordSent': 'Bu e-posta için hesap mevcutsa sıfırlama bağlantısı gönderildi.',
  'auth.sendResetLink': 'Sıfırlama Bağlantısı Gönder',
  'auth.sending': 'Gönderiliyor...',
  'auth.backToLogin': 'Girişe dön',
  'auth.resetPassword': 'Şifreyi Sıfırla',
  'auth.newPassword': 'Yeni Şifre',
  'auth.confirmPassword': 'Şifreyi Onayla',
  'auth.passwordMismatch': 'Şifreler eşleşmiyor',
  'auth.passwordTooShort': 'Şifre en az 6 karakter olmalıdır',
  'auth.resetSuccess': 'Şifre güncellendi! Giriş sayfasına yönlendiriliyor...',
  'auth.resetInvalidLink': 'Geçersiz veya eksik sıfırlama bağlantısı.',
  'auth.saving': 'Kaydediliyor...',
  'nav.leaderboard': 'Skor Tablosu',
  'leaderboard.title': 'Skor Tablosu',
  'leaderboard.bySong': 'Şarkı Bazında',
  'leaderboard.global': 'Genel',
  'leaderboard.rank': 'Sıra',
  'leaderboard.player': 'Oyuncu',
  'leaderboard.score': 'Puan',
  'leaderboard.accuracy': 'Doğruluk',
  'leaderboard.date': 'Tarih',
};

const FR: Dict = {
  'app.title': 'Musicska',
  'app.subtitle': 'Entraînement interactif des notes pour les instruments',
  'app.subtitleHint': 'Vous pouvez télécharger une image, un pdf ou un fichier mxl pour jouer ou vous entraîner',
  'app.signUp': 'Inscrivez-vous pour la compétition',
  'nav.home': 'Accueil',
  'nav.profile': 'Profil',
  'home.uploadImage': 'Charger une image (.jpg, .png, .pdf)',
  'home.uploadImageHint': 'Sélectionnez une image de partition ou un PDF à afficher',
  'home.uploadImageClear': "Supprimer l'image",
  'home.converting': 'Conversion en MusicXML via Audiveris...',
  'home.convertOk': 'Conversion terminée — partition chargée',
  'home.convertErr': 'Échec de la conversion',
  'home.convertErr.LOW_RESOLUTION': "La résolution de l'image est trop faible pour la reconnaissance des notes. Utilisez un scan de meilleure résolution (environ 300 DPI) ou importez le PDF.",
  'home.convertErr.NO_SCORE': "Aucune partition lisible (lignes de portée) n'a été détectée dans ce fichier.",
  'home.convertErr.CONVERT_FAILED': "Impossible de convertir ce fichier. Assurez-vous qu'il s'agit d'une partition imprimée nette.",
  'home.upload': 'Charger un fichier .mxl',
  'home.uploadHint': 'Choisissez un fichier MusicXML compressé (.mxl) depuis votre ordinateur',
  'home.noScore': 'Aucune partition chargée. Chargez un fichier .mxl pour commencer.',
  'home.bpm': 'Tempo (BPM)',
  'home.play': 'Lire',
  'home.stop': 'Arrêter',
  'home.record': 'Testez-vous',
  'home.stopRecord': "Arrêter l'enregistrement",
  'home.reset': 'Réinitialiser',
  'home.micStart': 'Activer le micro',
  'home.micStop': 'Arrêter le micro',
  'home.currentNote': 'Note actuelle',
  'home.target': 'Cible',
  'home.detected': 'Détectée',
  'home.waiting': 'En attente de son...',
  'home.completed': 'Terminé !',
  'home.correct': 'Correct',
  'home.accuracy': 'Précision',
  'home.totalScore': 'Score total',
  'home.micError': 'Erreur de microphone',
  'home.loadError': 'Impossible de charger la partition',
  'home.flat': 'grave',
  'home.sharp': 'aigu',
  'home.metronome': 'Métronome',
  'home.loading': 'Chargement de la partition...',
  'home.countdown': 'Préparez-vous...',
  'profile.title': 'Profil',
  'profile.language': "Langue de l'interface",
  'profile.noteNaming': 'Nomenclature des notes',
  'profile.noteNaming.abc': 'Lettres (A4, B4, C4)',
  'profile.noteNaming.dorm': 'Solfège (do4, re4, mi4)',
  'profile.defaultBpm': 'Tempo par défaut (BPM)',
  'profile.saved': 'Enregistré',
  'lang.en': 'English',
  'lang.tr': 'Türkçe',
  'lang.fr': 'Français',
  'lang.de': 'Deutsch',
  'lang.es': 'Español',
  'lang.it': 'Italiano',
  'lang.zh': '中文',
  'lang.ja': '日本語',
  'lang.ko': '한국어',
  'lang.hi': 'हिन्दी',
  'lang.ru': 'Русский',
  'lang.ar': 'العربية',
  'lang.pt': 'Português',
  'auth.login': 'Se connecter',
  'auth.logout': 'Se déconnecter',
  'auth.register': "S'inscrire",
  'auth.email': 'E-mail',
  'auth.password': 'Mot de passe',
  'auth.name': 'Nom',
  'auth.noAccount': 'Pas de compte ?',
  'auth.hasAccount': 'Déjà un compte ?',
  'auth.loginGoogle': 'Se connecter avec Google',
  'auth.loginError': 'E-mail ou mot de passe invalide',
  'nav.leaderboard': 'Classement',
  'leaderboard.title': 'Classement',
  'leaderboard.bySong': 'Par chanson',
  'leaderboard.global': 'Général',
  'leaderboard.rank': 'Rang',
  'leaderboard.player': 'Joueur',
  'leaderboard.score': 'Score',
  'leaderboard.accuracy': 'Précision',
  'leaderboard.date': 'Date',
};

const DE: Dict = {
  'app.title': 'Musicska',
  'app.subtitle': 'Interaktives Notentraining für Instrumente',
  'app.subtitleHint': 'Sie können eine Bild-, PDF- oder MXL-Datei zum Spielen oder Üben hochladen',
  'app.signUp': 'Für den Wettbewerb anmelden',
  'nav.home': 'Start',
  'nav.profile': 'Profil',
  'home.uploadImage': 'Bilddatei hochladen (.jpg, .png, .pdf)',
  'home.uploadImageHint': 'Wähle ein Notenblatt-Bild oder PDF zum Anzeigen',
  'home.uploadImageClear': 'Bild entfernen',
  'home.converting': 'Bild wird mit Audiveris in MusicXML konvertiert...',
  'home.convertOk': 'Konvertierung abgeschlossen — Noten geladen',
  'home.convertErr': 'Konvertierung fehlgeschlagen',
  'home.convertErr.LOW_RESOLUTION': 'Die Bildauflösung ist für die Notenerkennung zu niedrig. Verwenden Sie einen höher aufgelösten Scan (etwa 300 DPI) oder laden Sie das PDF hoch.',
  'home.convertErr.NO_SCORE': 'In dieser Datei wurden keine lesbaren Noten (Notenlinien) erkannt.',
  'home.convertErr.CONVERT_FAILED': 'Diese Datei konnte nicht konvertiert werden. Stellen Sie sicher, dass es sich um klare, gedruckte Noten handelt.',
  'home.upload': '.mxl-Datei hochladen',
  'home.uploadHint': 'Wähle eine komprimierte MusicXML-Datei (.mxl) von deinem Computer',
  'home.noScore': 'Noch keine Noten geladen. Lade eine .mxl-Datei, um zu beginnen.',
  'home.bpm': 'Tempo (BPM)',
  'home.play': 'Abspielen',
  'home.stop': 'Stopp',
  'home.record': 'Selbst testen',
  'home.stopRecord': 'Aufnahme stoppen',
  'home.reset': 'Zurücksetzen',
  'home.micStart': 'Mikrofon starten',
  'home.micStop': 'Mikrofon stoppen',
  'home.currentNote': 'Aktuelle Note',
  'home.target': 'Ziel',
  'home.detected': 'Erkannt',
  'home.waiting': 'Warte auf Ton...',
  'home.completed': 'Fertig!',
  'home.correct': 'Richtig',
  'home.accuracy': 'Genauigkeit',
  'home.totalScore': 'Gesamtpunktzahl',
  'home.micError': 'Mikrofonfehler',
  'home.loadError': 'Noten konnten nicht geladen werden',
  'home.flat': 'zu tief',
  'home.sharp': 'zu hoch',
  'home.metronome': 'Metronom',
  'home.loading': 'Noten werden geladen...',
  'home.countdown': 'Mach dich bereit...',
  'profile.title': 'Profil',
  'profile.language': 'Sprache der Oberfläche',
  'profile.noteNaming': 'Notenbenennung',
  'profile.noteNaming.abc': 'Buchstaben (A4, B4, C4)',
  'profile.noteNaming.dorm': 'Solfège (do4, re4, mi4)',
  'profile.defaultBpm': 'Standardtempo (BPM)',
  'profile.saved': 'Gespeichert',
  'lang.en': 'English',
  'lang.tr': 'Türkçe',
  'lang.fr': 'Français',
  'lang.de': 'Deutsch',
  'lang.es': 'Español',
  'lang.it': 'Italiano',
  'lang.zh': '中文',
  'lang.ja': '日本語',
  'lang.ko': '한국어',
  'lang.hi': 'हिन्दी',
  'lang.ru': 'Русский',
  'lang.ar': 'العربية',
  'lang.pt': 'Português',
  'auth.login': 'Anmelden',
  'auth.logout': 'Abmelden',
  'auth.register': 'Registrieren',
  'auth.email': 'E-Mail',
  'auth.password': 'Passwort',
  'auth.name': 'Name',
  'auth.noAccount': 'Kein Konto?',
  'auth.hasAccount': 'Schon ein Konto?',
  'auth.loginGoogle': 'Mit Google anmelden',
  'auth.loginError': 'Ungültige E-Mail oder Passwort',
  'nav.leaderboard': 'Rangliste',
  'leaderboard.title': 'Rangliste',
  'leaderboard.bySong': 'Nach Lied',
  'leaderboard.global': 'Gesamt',
  'leaderboard.rank': 'Rang',
  'leaderboard.player': 'Spieler',
  'leaderboard.score': 'Punkte',
  'leaderboard.accuracy': 'Genauigkeit',
  'leaderboard.date': 'Datum',
};

const ES: Dict = {
  'app.title': 'Musicska',
  'app.subtitle': 'Entrenamiento interactivo de notas para instrumentos',
  'app.subtitleHint': 'Puedes subir una imagen, pdf o archivo mxl para tocar o entrenar',
  'app.signUp': 'Regístrate para la competición',
  'nav.home': 'Inicio',
  'nav.profile': 'Perfil',
  'home.uploadImage': 'Cargar imagen (.jpg, .png, .pdf)',
  'home.uploadImageHint': 'Selecciona una imagen de partitura o PDF para mostrar',
  'home.uploadImageClear': 'Quitar imagen',
  'home.converting': 'Convirtiendo imagen a MusicXML con Audiveris...',
  'home.convertOk': 'Conversión completa — partitura cargada',
  'home.convertErr': 'Error de conversión',
  'home.convertErr.LOW_RESOLUTION': 'La resolución de la imagen es demasiado baja para el reconocimiento de notas. Use un escaneo de mayor resolución (unos 300 DPI) o suba el PDF.',
  'home.convertErr.NO_SCORE': 'No se detectó partitura legible (líneas de pentagrama) en este archivo.',
  'home.convertErr.CONVERT_FAILED': 'No se pudo convertir este archivo. Asegúrese de que sea una partitura impresa y nítida.',
  'home.upload': 'Cargar archivo .mxl',
  'home.uploadHint': 'Elige un MusicXML comprimido (.mxl) desde tu ordenador',
  'home.noScore': 'Aún no hay partitura cargada. Carga un archivo .mxl para comenzar.',
  'home.bpm': 'Tempo (BPM)',
  'home.play': 'Reproducir',
  'home.stop': 'Detener',
  'home.record': 'Pruébate',
  'home.stopRecord': 'Detener grabación',
  'home.reset': 'Reiniciar',
  'home.micStart': 'Activar micrófono',
  'home.micStop': 'Detener micrófono',
  'home.currentNote': 'Nota actual',
  'home.target': 'Objetivo',
  'home.detected': 'Detectada',
  'home.waiting': 'Esperando sonido...',
  'home.completed': '¡Completado!',
  'home.correct': 'Correcto',
  'home.accuracy': 'Precisión',
  'home.totalScore': 'Puntuación total',
  'home.micError': 'Error de micrófono',
  'home.loadError': 'No se pudo cargar la partitura',
  'home.flat': 'bajo',
  'home.sharp': 'alto',
  'home.metronome': 'Metrónomo',
  'home.loading': 'Cargando partitura...',
  'home.countdown': 'Prepárate...',
  'profile.title': 'Perfil',
  'profile.language': 'Idioma de la interfaz',
  'profile.noteNaming': 'Nomenclatura de notas',
  'profile.noteNaming.abc': 'Letras (A4, B4, C4)',
  'profile.noteNaming.dorm': 'Solfeo (do4, re4, mi4)',
  'profile.defaultBpm': 'Tempo predeterminado (BPM)',
  'profile.saved': 'Guardado',
  'lang.en': 'English',
  'lang.tr': 'Türkçe',
  'lang.fr': 'Français',
  'lang.de': 'Deutsch',
  'lang.es': 'Español',
  'lang.it': 'Italiano',
  'lang.zh': '中文',
  'lang.ja': '日本語',
  'lang.ko': '한국어',
  'lang.hi': 'हिन्दी',
  'lang.ru': 'Русский',
  'lang.ar': 'العربية',
  'lang.pt': 'Português',
  'auth.login': 'Iniciar sesión',
  'auth.logout': 'Cerrar sesión',
  'auth.register': 'Registrarse',
  'auth.email': 'Correo electrónico',
  'auth.password': 'Contraseña',
  'auth.name': 'Nombre',
  'auth.noAccount': '¿Sin cuenta?',
  'auth.hasAccount': '¿Ya tienes cuenta?',
  'auth.loginGoogle': 'Entrar con Google',
  'auth.loginError': 'Correo o contraseña inválidos',
  'nav.leaderboard': 'Clasificación',
  'leaderboard.title': 'Clasificación',
  'leaderboard.bySong': 'Por canción',
  'leaderboard.global': 'Global',
  'leaderboard.rank': 'Posición',
  'leaderboard.player': 'Jugador',
  'leaderboard.score': 'Puntuación',
  'leaderboard.accuracy': 'Precisión',
  'leaderboard.date': 'Fecha',
};

const IT: Dict = {
  'app.title': 'Musicska',
  'app.subtitle': 'Allenamento interattivo delle note per strumenti',
  'app.subtitleHint': "Puoi caricare un'immagine, un pdf o un file mxl per suonare o allenarti",
  'app.signUp': 'Iscriviti per la competizione',
  'nav.home': 'Home',
  'nav.profile': 'Profilo',
  'home.uploadImage': 'Carica immagine (.jpg, .png, .pdf)',
  'home.uploadImageHint': "Scegli un'immagine di partitura o PDF da visualizzare",
  'home.uploadImageClear': 'Rimuovi immagine',
  'home.converting': 'Conversione in MusicXML tramite Audiveris...',
  'home.convertOk': 'Conversione completata — partitura caricata',
  'home.convertErr': 'Conversione fallita',
  'home.convertErr.LOW_RESOLUTION': "La risoluzione dell'immagine è troppo bassa per il riconoscimento delle note. Usa una scansione a risoluzione più alta (circa 300 DPI) o carica il PDF.",
  'home.convertErr.NO_SCORE': 'Nessuno spartito leggibile (righi musicali) rilevato in questo file.',
  'home.convertErr.CONVERT_FAILED': 'Impossibile convertire questo file. Assicurati che sia uno spartito stampato e nitido.',
  'home.upload': 'Carica file .mxl',
  'home.uploadHint': 'Scegli un MusicXML compresso (.mxl) dal tuo computer',
  'home.noScore': 'Nessuna partitura caricata. Carica un file .mxl per iniziare.',
  'home.bpm': 'Tempo (BPM)',
  'home.play': 'Riproduci',
  'home.stop': 'Ferma',
  'home.record': 'Mettiti alla prova',
  'home.stopRecord': 'Ferma registrazione',
  'home.reset': 'Reimposta',
  'home.micStart': 'Avvia microfono',
  'home.micStop': 'Ferma microfono',
  'home.currentNote': 'Nota attuale',
  'home.target': 'Obiettivo',
  'home.detected': 'Rilevata',
  'home.waiting': 'In attesa di suono...',
  'home.completed': 'Completato!',
  'home.correct': 'Corretto',
  'home.accuracy': 'Precisione',
  'home.totalScore': 'Punteggio totale',
  'home.micError': 'Errore microfono',
  'home.loadError': 'Impossibile caricare la partitura',
  'home.flat': 'basso',
  'home.sharp': 'alto',
  'home.metronome': 'Metronomo',
  'home.loading': 'Caricamento partitura...',
  'home.countdown': 'Preparati...',
  'profile.title': 'Profilo',
  'profile.language': "Lingua dell'interfaccia",
  'profile.noteNaming': 'Nomenclatura delle note',
  'profile.noteNaming.abc': 'Lettere (A4, B4, C4)',
  'profile.noteNaming.dorm': 'Solfeggio (do4, re4, mi4)',
  'profile.defaultBpm': 'Tempo predefinito (BPM)',
  'profile.saved': 'Salvato',
  'lang.en': 'English',
  'lang.tr': 'Türkçe',
  'lang.fr': 'Français',
  'lang.de': 'Deutsch',
  'lang.es': 'Español',
  'lang.it': 'Italiano',
  'lang.zh': '中文',
  'lang.ja': '日本語',
  'lang.ko': '한국어',
  'lang.hi': 'हिन्दी',
  'lang.ru': 'Русский',
  'lang.ar': 'العربية',
  'lang.pt': 'Português',
  'auth.login': 'Accedi',
  'auth.logout': 'Esci',
  'auth.register': 'Registrati',
  'auth.email': 'E-mail',
  'auth.password': 'Password',
  'auth.name': 'Nome',
  'auth.noAccount': 'Nessun account?',
  'auth.hasAccount': 'Hai già un account?',
  'auth.loginGoogle': 'Accedi con Google',
  'auth.loginError': 'E-mail o password non validi',
  'nav.leaderboard': 'Classifica',
  'leaderboard.title': 'Classifica',
  'leaderboard.bySong': 'Per canzone',
  'leaderboard.global': 'Globale',
  'leaderboard.rank': 'Posizione',
  'leaderboard.player': 'Giocatore',
  'leaderboard.score': 'Punteggio',
  'leaderboard.accuracy': 'Precisione',
  'leaderboard.date': 'Data',
};

const ZH: Dict = {
  'app.title': 'Musicska',
  'app.subtitle': '乐器互动音符训练',
  'app.subtitleHint': '您可以上传图片、PDF或MXL文件进行演奏或训练',
  'app.signUp': '注册参加比赛',
  'nav.home': '主页',
  'nav.profile': '个人资料',
  'home.uploadImage': '上传图片文件（.jpg, .png, .pdf）',
  'home.uploadImageHint': '选择要显示的乐谱图片或PDF',
  'home.uploadImageClear': '移除图片',
  'home.converting': '正在通过Audiveris将图片转换为MusicXML…',
  'home.convertOk': '转换完成 — 乐谱已加载',
  'home.convertErr': '转换失败',
  'home.convertErr.LOW_RESOLUTION': '图片分辨率太低，无法识别音符。请使用更高分辨率的扫描件（约 300 DPI）或上传 PDF。',
  'home.convertErr.NO_SCORE': '在此文件中未检测到可识别的乐谱（五线谱线）。',
  'home.convertErr.CONVERT_FAILED': '无法转换此文件。请确认它是清晰的印刷乐谱。',
  'home.upload': '上传 .mxl 文件',
  'home.uploadHint': '点击从计算机选择压缩的MusicXML（.mxl）文件',
  'home.noScore': '尚未加载乐谱，请上传 .mxl 文件以开始。',
  'home.bpm': '速度（BPM）',
  'home.play': '播放',
  'home.stop': '停止',
  'home.record': '自我测试',
  'home.stopRecord': '停止录音',
  'home.reset': '重置',
  'home.micStart': '开启麦克风',
  'home.micStop': '关闭麦克风',
  'home.currentNote': '当前音符',
  'home.target': '目标',
  'home.detected': '已检测',
  'home.waiting': '等待声音…',
  'home.completed': '完成！',
  'home.correct': '正确',
  'home.accuracy': '准确率',
  'home.totalScore': '总分',
  'home.micError': '麦克风错误',
  'home.loadError': '无法加载乐谱',
  'home.flat': '降',
  'home.sharp': '升',
  'home.metronome': '节拍器',
  'home.loading': '正在加载乐谱…',
  'home.countdown': '准备好…',
  'profile.title': '个人资料',
  'profile.language': '界面语言',
  'profile.noteNaming': '音符命名',
  'profile.noteNaming.abc': '字母名称（A4, B4, C4）',
  'profile.noteNaming.dorm': '唱名（do4, re4, mi4）',
  'profile.defaultBpm': '默认速度（BPM）',
  'profile.saved': '已保存',
  'lang.en': 'English',
  'lang.tr': 'Türkçe',
  'lang.fr': 'Français',
  'lang.de': 'Deutsch',
  'lang.es': 'Español',
  'lang.it': 'Italiano',
  'lang.zh': '中文',
  'lang.ja': '日本語',
  'lang.ko': '한국어',
  'lang.hi': 'हिन्दी',
  'lang.ru': 'Русский',
  'lang.ar': 'العربية',
  'lang.pt': 'Português',
  'auth.login': '登录',
  'auth.logout': '退出',
  'auth.register': '注册',
  'auth.email': '邮箱',
  'auth.password': '密码',
  'auth.name': '姓名',
  'auth.noAccount': '没有账号？',
  'auth.hasAccount': '已有账号？',
  'auth.loginGoogle': '使用Google登录',
  'auth.loginError': '邮箱或密码无效',
  'nav.leaderboard': '排行榜',
  'leaderboard.title': '排行榜',
  'leaderboard.bySong': '按歌曲',
  'leaderboard.global': '全球',
  'leaderboard.rank': '排名',
  'leaderboard.player': '玩家',
  'leaderboard.score': '分数',
  'leaderboard.accuracy': '准确率',
  'leaderboard.date': '日期',
};

const JA: Dict = {
  'app.title': 'Musicska',
  'app.subtitle': '楽器のためのインタラクティブ音符トレーニング',
  'app.subtitleHint': '画像、PDF、またはMXLファイルをアップロードして演奏や練習ができます',
  'app.signUp': 'コンテストに参加登録する',
  'nav.home': 'ホーム',
  'nav.profile': 'プロフィール',
  'home.uploadImage': '画像ファイルをアップロード（.jpg, .png, .pdf）',
  'home.uploadImageHint': '表示する楽譜の画像またはPDFを選択',
  'home.uploadImageClear': '画像を削除',
  'home.converting': 'AudiverisでMusicXMLに変換中…',
  'home.convertOk': '変換完了 — 楽譜を読み込みました',
  'home.convertErr': '変換に失敗しました',
  'home.convertErr.LOW_RESOLUTION': '画像の解像度が低すぎて音符を認識できません。より高解像度のスキャン（約300 DPI）を使うか、PDFをアップロードしてください。',
  'home.convertErr.NO_SCORE': 'このファイルから読み取れる楽譜（五線）が検出されませんでした。',
  'home.convertErr.CONVERT_FAILED': 'このファイルを変換できませんでした。鮮明な印刷楽譜であることを確認してください。',
  'home.upload': '.mxlファイルをアップロード',
  'home.uploadHint': 'クリックしてPCから圧縮MusicXML（.mxl）を選択',
  'home.noScore': 'まだ楽譜がありません。.mxlファイルをアップロードして開始してください。',
  'home.bpm': 'テンポ（BPM）',
  'home.play': '再生',
  'home.stop': '停止',
  'home.record': '自己テスト',
  'home.stopRecord': '録音を停止',
  'home.reset': 'リセット',
  'home.micStart': 'マイクを開始',
  'home.micStop': 'マイクを停止',
  'home.currentNote': '現在の音符',
  'home.target': 'ターゲット',
  'home.detected': '検出',
  'home.waiting': '音を待っています…',
  'home.completed': '完了！',
  'home.correct': '正解',
  'home.accuracy': '正確度',
  'home.totalScore': '合計スコア',
  'home.micError': 'マイクエラー',
  'home.loadError': '楽譜を読み込めませんでした',
  'home.flat': '低め',
  'home.sharp': '高め',
  'home.metronome': 'メトロノーム',
  'home.loading': '楽譜を読み込み中…',
  'home.countdown': '準備して…',
  'profile.title': 'プロフィール',
  'profile.language': '表示言語',
  'profile.noteNaming': '音符の表記',
  'profile.noteNaming.abc': 'アルファベット（A4, B4, C4）',
  'profile.noteNaming.dorm': '階名（do4, re4, mi4）',
  'profile.defaultBpm': 'デフォルトテンポ（BPM）',
  'profile.saved': '保存しました',
  'lang.en': 'English',
  'lang.tr': 'Türkçe',
  'lang.fr': 'Français',
  'lang.de': 'Deutsch',
  'lang.es': 'Español',
  'lang.it': 'Italiano',
  'lang.zh': '中文',
  'lang.ja': '日本語',
  'lang.ko': '한국어',
  'lang.hi': 'हिन्दी',
  'lang.ru': 'Русский',
  'lang.ar': 'العربية',
  'lang.pt': 'Português',
  'auth.login': 'ログイン',
  'auth.logout': 'ログアウト',
  'auth.register': '登録',
  'auth.email': 'メールアドレス',
  'auth.password': 'パスワード',
  'auth.name': '名前',
  'auth.noAccount': 'アカウントをお持ちでない方',
  'auth.hasAccount': 'すでにアカウントをお持ちの方',
  'auth.loginGoogle': 'Googleでログイン',
  'auth.loginError': 'メールアドレスまたはパスワードが正しくありません',
  'nav.leaderboard': 'ランキング',
  'leaderboard.title': 'ランキング',
  'leaderboard.bySong': '曲別',
  'leaderboard.global': '総合',
  'leaderboard.rank': '順位',
  'leaderboard.player': 'プレイヤー',
  'leaderboard.score': 'スコア',
  'leaderboard.accuracy': '正確度',
  'leaderboard.date': '日付',
};

const KO: Dict = {
  'app.title': 'Musicska',
  'app.subtitle': '악기를 위한 대화형 음표 훈련',
  'app.subtitleHint': '연주나 훈련을 위해 이미지, PDF 또는 MXL 파일을 업로드할 수 있습니다',
  'app.signUp': '대회 참가를 위해 가입하세요',
  'nav.home': '홈',
  'nav.profile': '프로필',
  'home.uploadImage': '이미지 파일 업로드 (.jpg, .png, .pdf)',
  'home.uploadImageHint': '표시할 악보 이미지 또는 PDF 선택',
  'home.uploadImageClear': '이미지 제거',
  'home.converting': 'Audiveris로 MusicXML 변환 중…',
  'home.convertOk': '변환 완료 — 악보 로드됨',
  'home.convertErr': '변환 실패',
  'home.convertErr.LOW_RESOLUTION': '이미지 해상도가 너무 낮아 음표를 인식할 수 없습니다. 더 높은 해상도의 스캔(약 300 DPI)을 사용하거나 PDF를 업로드하세요.',
  'home.convertErr.NO_SCORE': '이 파일에서 읽을 수 있는 악보(오선)를 찾지 못했습니다.',
  'home.convertErr.CONVERT_FAILED': '이 파일을 변환할 수 없습니다. 선명한 인쇄 악보인지 확인하세요.',
  'home.upload': '.mxl 파일 업로드',
  'home.uploadHint': '클릭하여 컴퓨터에서 압축 MusicXML(.mxl) 선택',
  'home.noScore': '아직 악보가 없습니다. .mxl 파일을 업로드하여 시작하세요.',
  'home.bpm': '템포 (BPM)',
  'home.play': '재생',
  'home.stop': '정지',
  'home.record': '자기 테스트',
  'home.stopRecord': '녹음 중지',
  'home.reset': '초기화',
  'home.micStart': '마이크 시작',
  'home.micStop': '마이크 중지',
  'home.currentNote': '현재 음표',
  'home.target': '목표',
  'home.detected': '감지됨',
  'home.waiting': '소리를 기다리는 중…',
  'home.completed': '완료!',
  'home.correct': '정확',
  'home.accuracy': '정확도',
  'home.totalScore': '총점',
  'home.micError': '마이크 오류',
  'home.loadError': '악보를 불러올 수 없습니다',
  'home.flat': '내림',
  'home.sharp': '올림',
  'home.metronome': '메트로놈',
  'home.loading': '악보 로딩 중…',
  'home.countdown': '준비하세요…',
  'profile.title': '프로필',
  'profile.language': '인터페이스 언어',
  'profile.noteNaming': '음표 표기법',
  'profile.noteNaming.abc': '알파벳 표기 (A4, B4, C4)',
  'profile.noteNaming.dorm': '계이름 (do4, re4, mi4)',
  'profile.defaultBpm': '기본 템포 (BPM)',
  'profile.saved': '저장됨',
  'lang.en': 'English',
  'lang.tr': 'Türkçe',
  'lang.fr': 'Français',
  'lang.de': 'Deutsch',
  'lang.es': 'Español',
  'lang.it': 'Italiano',
  'lang.zh': '中文',
  'lang.ja': '日本語',
  'lang.ko': '한국어',
  'lang.hi': 'हिन्दी',
  'lang.ru': 'Русский',
  'lang.ar': 'العربية',
  'lang.pt': 'Português',
  'auth.login': '로그인',
  'auth.logout': '로그아웃',
  'auth.register': '회원가입',
  'auth.email': '이메일',
  'auth.password': '비밀번호',
  'auth.name': '이름',
  'auth.noAccount': '계정이 없으신가요?',
  'auth.hasAccount': '이미 계정이 있으신가요?',
  'auth.loginGoogle': 'Google로 로그인',
  'auth.loginError': '이메일 또는 비밀번호가 올바르지 않습니다',
  'nav.leaderboard': '리더보드',
  'leaderboard.title': '리더보드',
  'leaderboard.bySong': '곡별',
  'leaderboard.global': '전체',
  'leaderboard.rank': '순위',
  'leaderboard.player': '플레이어',
  'leaderboard.score': '점수',
  'leaderboard.accuracy': '정확도',
  'leaderboard.date': '날짜',
};

const HI: Dict = {
  'app.title': 'Musicska',
  'app.subtitle': 'वाद्ययंत्रों के लिए इंटरेक्टिव नोट प्रशिक्षण',
  'app.subtitleHint': 'आप बजाने या प्रशिक्षण के लिए छवि, PDF या MXL फ़ाइल अपलोड कर सकते हैं',
  'app.signUp': 'प्रतियोगिता के लिए साइन अप करें',
  'nav.home': 'होम',
  'nav.profile': 'प्रोफ़ाइल',
  'home.uploadImage': 'इमेज फ़ाइल अपलोड करें (.jpg, .png, .pdf)',
  'home.uploadImageHint': 'दिखाने के लिए शीट म्यूज़िक इमेज या PDF चुनें',
  'home.uploadImageClear': 'इमेज हटाएं',
  'home.converting': 'Audiveris से MusicXML में बदला जा रहा है…',
  'home.convertOk': 'रूपांतरण पूर्ण — स्कोर लोड हो गया',
  'home.convertErr': 'रूपांतरण विफल',
  'home.convertErr.LOW_RESOLUTION': 'नोट पहचान के लिए छवि का रिज़ॉल्यूशन बहुत कम है। उच्च रिज़ॉल्यूशन वाला स्कैन (लगभग 300 DPI) उपयोग करें या PDF अपलोड करें।',
  'home.convertErr.NO_SCORE': 'इस फ़ाइल में पढ़ने योग्य स्वरलिपि (स्टाफ़ लाइनें) नहीं मिलीं।',
  'home.convertErr.CONVERT_FAILED': 'इस फ़ाइल को रूपांतरित नहीं किया जा सका। सुनिश्चित करें कि यह स्पष्ट, मुद्रित स्वरलिपि है।',
  'home.upload': '.mxl फ़ाइल अपलोड करें',
  'home.uploadHint': 'कंप्यूटर से कम्प्रेस्ड MusicXML (.mxl) चुनने के लिए क्लिक करें',
  'home.noScore': 'अभी कोई स्कोर नहीं है। शुरू करने के लिए .mxl फ़ाइल अपलोड करें।',
  'home.bpm': 'गति (BPM)',
  'home.play': 'चलाएं',
  'home.stop': 'रोकें',
  'home.record': 'स्वयं परीक्षण',
  'home.stopRecord': 'रिकॉर्डिंग रोकें',
  'home.reset': 'रीसेट',
  'home.micStart': 'माइक शुरू करें',
  'home.micStop': 'माइक बंद करें',
  'home.currentNote': 'वर्तमान नोट',
  'home.target': 'लक्ष्य',
  'home.detected': 'पहचाना गया',
  'home.waiting': 'आवाज़ का इंतज़ार…',
  'home.completed': 'पूर्ण!',
  'home.correct': 'सही',
  'home.accuracy': 'सटीकता',
  'home.totalScore': 'कुल स्कोर',
  'home.micError': 'माइक त्रुटि',
  'home.loadError': 'स्कोर लोड नहीं हो सका',
  'home.flat': 'कोमल',
  'home.sharp': 'तीव्र',
  'home.metronome': 'मेट्रोनोम',
  'home.loading': 'स्कोर लोड हो रहा है…',
  'home.countdown': 'तैयार हो जाएं…',
  'profile.title': 'प्रोफ़ाइल',
  'profile.language': 'इंटरफ़ेस भाषा',
  'profile.noteNaming': 'नोट नामकरण',
  'profile.noteNaming.abc': 'अक्षर नाम (A4, B4, C4)',
  'profile.noteNaming.dorm': 'सरगम (do4, re4, mi4)',
  'profile.defaultBpm': 'डिफ़ॉल्ट गति (BPM)',
  'profile.saved': 'सहेजा गया',
  'lang.en': 'English',
  'lang.tr': 'Türkçe',
  'lang.fr': 'Français',
  'lang.de': 'Deutsch',
  'lang.es': 'Español',
  'lang.it': 'Italiano',
  'lang.zh': '中文',
  'lang.ja': '日本語',
  'lang.ko': '한국어',
  'lang.hi': 'हिन्दी',
  'lang.ru': 'Русский',
  'lang.ar': 'العربية',
  'lang.pt': 'Português',
  'auth.login': 'लॉग इन',
  'auth.logout': 'लॉग आउट',
  'auth.register': 'पंजीकरण',
  'auth.email': 'ईमेल',
  'auth.password': 'पासवर्ड',
  'auth.name': 'नाम',
  'auth.noAccount': 'खाता नहीं है?',
  'auth.hasAccount': 'पहले से खाता है?',
  'auth.loginGoogle': 'Google से लॉग इन करें',
  'auth.loginError': 'ईमेल या पासवर्ड गलत है',
  'nav.leaderboard': 'लीडरबोर्ड',
  'leaderboard.title': 'लीडरबोर्ड',
  'leaderboard.bySong': 'गाने के अनुसार',
  'leaderboard.global': 'वैश्विक',
  'leaderboard.rank': 'रैंक',
  'leaderboard.player': 'खिलाड़ी',
  'leaderboard.score': 'स्कोर',
  'leaderboard.accuracy': 'सटीकता',
  'leaderboard.date': 'तारीख',
};

const RU: Dict = {
  'app.title': 'Musicska',
  'app.subtitle': 'Интерактивная тренировка нот для музыкантов',
  'app.subtitleHint': 'Вы можете загрузить изображение, PDF или MXL-файл для игры или тренировки',
  'app.signUp': 'Зарегистрируйтесь для участия в соревновании',
  'nav.home': 'Главная',
  'nav.profile': 'Профиль',
  'home.uploadImage': 'Загрузить изображение (.jpg, .png, .pdf)',
  'home.uploadImageHint': 'Выберите изображение нотного листа или PDF',
  'home.uploadImageClear': 'Убрать изображение',
  'home.converting': 'Конвертация в MusicXML через Audiveris…',
  'home.convertOk': 'Конвертация завершена — ноты загружены',
  'home.convertErr': 'Ошибка конвертации',
  'home.convertErr.LOW_RESOLUTION': 'Разрешение изображения слишком низкое для распознавания нот. Используйте скан с более высоким разрешением (около 300 DPI) или загрузите PDF.',
  'home.convertErr.NO_SCORE': 'В этом файле не обнаружены читаемые ноты (нотный стан).',
  'home.convertErr.CONVERT_FAILED': 'Не удалось преобразовать этот файл. Убедитесь, что это чёткие печатные ноты.',
  'home.upload': 'Загрузить .mxl файл',
  'home.uploadHint': 'Нажмите, чтобы выбрать сжатый MusicXML (.mxl) с компьютера',
  'home.noScore': 'Ноты не загружены. Загрузите .mxl файл, чтобы начать.',
  'home.bpm': 'Темп (BPM)',
  'home.play': 'Воспроизвести',
  'home.stop': 'Стоп',
  'home.record': 'Проверь себя',
  'home.stopRecord': 'Остановить запись',
  'home.reset': 'Сбросить',
  'home.micStart': 'Включить микрофон',
  'home.micStop': 'Выключить микрофон',
  'home.currentNote': 'Текущая нота',
  'home.target': 'Цель',
  'home.detected': 'Определено',
  'home.waiting': 'Ожидание звука…',
  'home.completed': 'Готово!',
  'home.correct': 'Верно',
  'home.accuracy': 'Точность',
  'home.totalScore': 'Общий счёт',
  'home.micError': 'Ошибка микрофона',
  'home.loadError': 'Не удалось загрузить ноты',
  'home.flat': 'пониженная',
  'home.sharp': 'повышенная',
  'home.metronome': 'Метроном',
  'home.loading': 'Загрузка нот…',
  'home.countdown': 'Приготовьтесь…',
  'profile.title': 'Профиль',
  'profile.language': 'Язык интерфейса',
  'profile.noteNaming': 'Названия нот',
  'profile.noteNaming.abc': 'Буквенные (A4, B4, C4)',
  'profile.noteNaming.dorm': 'Сольфеджио (do4, re4, mi4)',
  'profile.defaultBpm': 'Темп по умолчанию (BPM)',
  'profile.saved': 'Сохранено',
  'lang.en': 'English',
  'lang.tr': 'Türkçe',
  'lang.fr': 'Français',
  'lang.de': 'Deutsch',
  'lang.es': 'Español',
  'lang.it': 'Italiano',
  'lang.zh': '中文',
  'lang.ja': '日本語',
  'lang.ko': '한국어',
  'lang.hi': 'हिन्दी',
  'lang.ru': 'Русский',
  'lang.ar': 'العربية',
  'lang.pt': 'Português',
  'auth.login': 'Войти',
  'auth.logout': 'Выйти',
  'auth.register': 'Зарегистрироваться',
  'auth.email': 'Эл. почта',
  'auth.password': 'Пароль',
  'auth.name': 'Имя',
  'auth.noAccount': 'Нет аккаунта?',
  'auth.hasAccount': 'Уже есть аккаунт?',
  'auth.loginGoogle': 'Войти через Google',
  'auth.loginError': 'Неверный адрес почты или пароль',
  'nav.leaderboard': 'Рейтинг',
  'leaderboard.title': 'Рейтинг',
  'leaderboard.bySong': 'По треку',
  'leaderboard.global': 'Общий',
  'leaderboard.rank': 'Место',
  'leaderboard.player': 'Игрок',
  'leaderboard.score': 'Очки',
  'leaderboard.accuracy': 'Точность',
  'leaderboard.date': 'Дата',
};

const AR: Dict = {
  'app.title': 'Musicska',
  'app.subtitle': 'تدريب تفاعلي على النوتات للموسيقيين',
  'app.subtitleHint': 'يمكنك تحميل صورة أو ملف PDF أو MXL للعزف أو التدريب',
  'app.signUp': 'سجّل للمشاركة في المسابقة',
  'nav.home': 'الرئيسية',
  'nav.profile': 'الملف الشخصي',
  'home.uploadImage': 'رفع ملف صورة (.jpg, .png, .pdf)',
  'home.uploadImageHint': 'اختر صورة نوتة موسيقية أو PDF للعرض',
  'home.uploadImageClear': 'إزالة الصورة',
  'home.converting': 'جارٍ تحويل الصورة إلى MusicXML عبر Audiveris…',
  'home.convertOk': 'اكتمل التحويل — تم تحميل النوتة',
  'home.convertErr': 'فشل التحويل',
  'home.convertErr.LOW_RESOLUTION': 'دقة الصورة منخفضة جدًا للتعرف على النوتات. استخدم مسحًا بدقة أعلى (نحو 300 نقطة/بوصة) أو ارفع ملف PDF.',
  'home.convertErr.NO_SCORE': 'لم يتم العثور على نوتة موسيقية قابلة للقراءة (أسطر المدرج) في هذا الملف.',
  'home.convertErr.CONVERT_FAILED': 'تعذّر تحويل هذا الملف. تأكد من أنه نوتة موسيقية مطبوعة وواضحة.',
  'home.upload': 'رفع ملف .mxl',
  'home.uploadHint': 'انقر لاختيار ملف MusicXML مضغوط (.mxl) من جهازك',
  'home.noScore': 'لم يتم تحميل أي نوتة بعد. ارفع ملف .mxl للبدء.',
  'home.bpm': 'الإيقاع (BPM)',
  'home.play': 'تشغيل',
  'home.stop': 'إيقاف',
  'home.record': 'اختبر نفسك',
  'home.stopRecord': 'إيقاف التسجيل',
  'home.reset': 'إعادة ضبط',
  'home.micStart': 'تشغيل الميكروفون',
  'home.micStop': 'إيقاف الميكروفون',
  'home.currentNote': 'النوتة الحالية',
  'home.target': 'الهدف',
  'home.detected': 'تم الكشف',
  'home.waiting': 'في انتظار الصوت…',
  'home.completed': 'اكتمل!',
  'home.correct': 'صحيح',
  'home.accuracy': 'الدقة',
  'home.totalScore': 'المجموع',
  'home.micError': 'خطأ في الميكروفون',
  'home.loadError': 'تعذّر تحميل النوتة',
  'home.flat': 'خافض',
  'home.sharp': 'رافع',
  'home.metronome': 'ميترونوم',
  'home.loading': 'جارٍ تحميل النوتة…',
  'home.countdown': 'استعد…',
  'profile.title': 'الملف الشخصي',
  'profile.language': 'لغة الواجهة',
  'profile.noteNaming': 'تسمية النوتات',
  'profile.noteNaming.abc': 'الأحرف (A4, B4, C4)',
  'profile.noteNaming.dorm': 'السولفيج (do4, re4, mi4)',
  'profile.defaultBpm': 'الإيقاع الافتراضي (BPM)',
  'profile.saved': 'تم الحفظ',
  'lang.en': 'English',
  'lang.tr': 'Türkçe',
  'lang.fr': 'Français',
  'lang.de': 'Deutsch',
  'lang.es': 'Español',
  'lang.it': 'Italiano',
  'lang.zh': '中文',
  'lang.ja': '日本語',
  'lang.ko': '한국어',
  'lang.hi': 'हिन्दी',
  'lang.ru': 'Русский',
  'lang.ar': 'العربية',
  'lang.pt': 'Português',
  'auth.login': 'تسجيل الدخول',
  'auth.logout': 'تسجيل الخروج',
  'auth.register': 'إنشاء حساب',
  'auth.email': 'البريد الإلكتروني',
  'auth.password': 'كلمة المرور',
  'auth.name': 'الاسم',
  'auth.noAccount': 'ليس لديك حساب؟',
  'auth.hasAccount': 'لديك حساب بالفعل؟',
  'auth.loginGoogle': 'الدخول بواسطة Google',
  'auth.loginError': 'البريد الإلكتروني أو كلمة المرور غير صحيحة',
  'nav.leaderboard': 'لوحة الصدارة',
  'leaderboard.title': 'لوحة الصدارة',
  'leaderboard.bySong': 'حسب الأغنية',
  'leaderboard.global': 'عام',
  'leaderboard.rank': 'المرتبة',
  'leaderboard.player': 'اللاعب',
  'leaderboard.score': 'النقاط',
  'leaderboard.accuracy': 'الدقة',
  'leaderboard.date': 'التاريخ',
};

const PT: Dict = {
  'app.title': 'Musicska',
  'app.subtitle': 'Treinamento interativo de notas para instrumentos',
  'app.subtitleHint': 'Você pode fazer upload de uma imagem, pdf ou arquivo mxl para tocar ou treinar',
  'app.signUp': 'Cadastre-se para a competição',
  'nav.home': 'Início',
  'nav.profile': 'Perfil',
  'home.uploadImage': 'Carregar imagem (.jpg, .png, .pdf)',
  'home.uploadImageHint': 'Escolha uma imagem de partitura ou PDF para exibir',
  'home.uploadImageClear': 'Remover imagem',
  'home.converting': 'Convertendo imagem para MusicXML via Audiveris…',
  'home.convertOk': 'Conversão concluída — partitura carregada',
  'home.convertErr': 'Falha na conversão',
  'home.convertErr.LOW_RESOLUTION': 'A resolução da imagem é muito baixa para o reconhecimento de notas. Use uma digitalização de maior resolução (cerca de 300 DPI) ou envie o PDF.',
  'home.convertErr.NO_SCORE': 'Nenhuma partitura legível (linhas de pauta) foi detectada neste arquivo.',
  'home.convertErr.CONVERT_FAILED': 'Não foi possível converter este arquivo. Verifique se é uma partitura impressa e nítida.',
  'home.upload': 'Carregar arquivo .mxl',
  'home.uploadHint': 'Clique para selecionar um MusicXML compactado (.mxl) do seu computador',
  'home.noScore': 'Nenhuma partitura carregada. Carregue um arquivo .mxl para começar.',
  'home.bpm': 'Andamento (BPM)',
  'home.play': 'Reproduzir',
  'home.stop': 'Parar',
  'home.record': 'Teste-se',
  'home.stopRecord': 'Parar gravação',
  'home.reset': 'Reiniciar',
  'home.micStart': 'Ativar microfone',
  'home.micStop': 'Desativar microfone',
  'home.currentNote': 'Nota atual',
  'home.target': 'Alvo',
  'home.detected': 'Detectada',
  'home.waiting': 'Aguardando som…',
  'home.completed': 'Concluído!',
  'home.correct': 'Correto',
  'home.accuracy': 'Precisão',
  'home.totalScore': 'Pontuação total',
  'home.micError': 'Erro no microfone',
  'home.loadError': 'Não foi possível carregar a partitura',
  'home.flat': 'grave',
  'home.sharp': 'agudo',
  'home.metronome': 'Metrônomo',
  'home.loading': 'Carregando partitura…',
  'home.countdown': 'Prepare-se…',
  'profile.title': 'Perfil',
  'profile.language': 'Idioma da interface',
  'profile.noteNaming': 'Nomenclatura das notas',
  'profile.noteNaming.abc': 'Letras (A4, B4, C4)',
  'profile.noteNaming.dorm': 'Solfejo (do4, re4, mi4)',
  'profile.defaultBpm': 'Andamento padrão (BPM)',
  'profile.saved': 'Salvo',
  'lang.en': 'English',
  'lang.tr': 'Türkçe',
  'lang.fr': 'Français',
  'lang.de': 'Deutsch',
  'lang.es': 'Español',
  'lang.it': 'Italiano',
  'lang.zh': '中文',
  'lang.ja': '日本語',
  'lang.ko': '한국어',
  'lang.hi': 'हिन्दी',
  'lang.ru': 'Русский',
  'lang.ar': 'العربية',
  'lang.pt': 'Português',
  'auth.login': 'Entrar',
  'auth.logout': 'Sair',
  'auth.register': 'Cadastrar',
  'auth.email': 'E-mail',
  'auth.password': 'Senha',
  'auth.name': 'Nome',
  'auth.noAccount': 'Não tem conta?',
  'auth.hasAccount': 'Já tem uma conta?',
  'auth.loginGoogle': 'Entrar com Google',
  'auth.loginError': 'E-mail ou senha inválidos',
  'nav.leaderboard': 'Classificação',
  'leaderboard.title': 'Classificação',
  'leaderboard.bySong': 'Por música',
  'leaderboard.global': 'Global',
  'leaderboard.rank': 'Posição',
  'leaderboard.player': 'Jogador',
  'leaderboard.score': 'Pontuação',
  'leaderboard.accuracy': 'Precisão',
  'leaderboard.date': 'Data',
};

const DICTS: Record<Language, Dict> = {
  en: EN, tr: TR, fr: FR, de: DE, es: ES, it: IT,
  zh: ZH, ja: JA, ko: KO, hi: HI, ru: RU, ar: AR, pt: PT,
};

export function useT() {
  const { prefs } = usePreferences();
  const dict = DICTS[prefs.language] ?? EN;
  return (key: string) => dict[key] ?? EN[key] ?? key;
}
